import React, { useState, useEffect, useRef } from 'react';

const VaccinationFilter = ({ initialFilters = {}, onFilterChange, isVisible = true, onClose }) => {
    const filterRef = useRef(null);
    
    // State to match the fields in VaccinationTable
    const [filters, setFilters] = useState({
        id: initialFilters.id || '',
        vaccineId: initialFilters.vaccineId || '',
        vaccineName: initialFilters.vaccineName || '',
        patientId: initialFilters.patientId || '',
        dateAdministered: initialFilters.dateAdministered || '',
        nextDueDate: initialFilters.nextDueDate || '',
        doseNumber: initialFilters.doseNumber || '',
        administrationRoute: initialFilters.administrationRoute || '',
        administrationSite: initialFilters.administrationSite || '',
        lotNumber: initialFilters.lotNumber || '',
        manufacturerId: initialFilters.manufacturerId || '',
        expirationDate: initialFilters.expirationDate || '',
        administratorId: initialFilters.administratorId || '',
        status: initialFilters.status || '',
        notes: initialFilters.notes || '',
        vaccinationId: initialFilters.vaccinationId || '',
        createdAt: initialFilters.createdAt || '',
        updatedAt: initialFilters.updatedAt || ''
    });

    // Handle click outside to close filter
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target) && isVisible && onClose) {
                onClose();
            }
        };

        if (isVisible) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isVisible, onClose]);

    useEffect(() => {
        setFilters({
            id: initialFilters.id || '',
            vaccineId: initialFilters.vaccineId || '',
            vaccineName: initialFilters.vaccineName || '',
            patientId: initialFilters.patientId || '',
            dateAdministered: initialFilters.dateAdministered || '',
            nextDueDate: initialFilters.nextDueDate || '',
            doseNumber: initialFilters.doseNumber || '',
            administrationRoute: initialFilters.administrationRoute || '',
            administrationSite: initialFilters.administrationSite || '',
            lotNumber: initialFilters.lotNumber || '',
            manufacturerId: initialFilters.manufacturerId || '',
            expirationDate: initialFilters.expirationDate || '',
            administratorId: initialFilters.administratorId || '',
            status: initialFilters.status || '',
            notes: initialFilters.notes || '',
            vaccinationId: initialFilters.vaccinationId || '',
            createdAt: initialFilters.createdAt || '',
            updatedAt: initialFilters.updatedAt || ''
        });
    }, [initialFilters]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Normal handling for all fields
        const newFilters = { ...filters, [name]: value };
        
        setFilters(newFilters);
        onFilterChange(newFilters);
    };

    const resetFilters = () => {
        const resetState = {
            id: '',
            vaccineId: '',
            vaccineName: '',
            patientId: '',
            dateAdministered: '',
            nextDueDate: '',
            doseNumber: '',
            administrationRoute: '',
            administrationSite: '',
            lotNumber: '',
            manufacturerId: '',
            expirationDate: '',
            administratorId: '',
            status: '',
            notes: '',
            vaccinationId: '',
            createdAt: '',
            updatedAt: ''
        };

        setFilters(resetState);
        onFilterChange(resetState);
    };

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    return (
        <div className="card" ref={filterRef}>
            <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Vaccination Filters</h6>
                {onClose && (
                    <button 
                        type="button" 
                        className="btn-close" 
                        aria-label="Close"
                        onClick={onClose}
                    ></button>
                )}
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <label className="form-label">Vaccination ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="id"
                            value={filters.id}
                            onChange={handleInputChange}
                            placeholder="Filter by Vaccination ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Vaccine ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="vaccineId"
                            value={filters.vaccineId}
                            onChange={handleInputChange}
                            placeholder="Filter by Vaccine ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Vaccine Name</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="vaccineName"
                            value={filters.vaccineName}
                            onChange={handleInputChange}
                            placeholder="Filter by Vaccine Name"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Patient ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="patientId"
                            value={filters.patientId}
                            onChange={handleInputChange}
                            placeholder="Filter by Patient ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Date Administered</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            name="dateAdministered"
                            value={filters.dateAdministered}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Next Due Date</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            name="nextDueDate"
                            value={filters.nextDueDate}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Dose Number</label>
                        <input 
                            type="number" 
                            className="form-control" 
                            name="doseNumber"
                            value={filters.doseNumber}
                            onChange={handleInputChange}
                            placeholder="Filter by Dose Number"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Administration Route</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="administrationRoute"
                            value={filters.administrationRoute}
                            onChange={handleInputChange}
                            placeholder="Filter by Administration Route"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Administration Site</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="administrationSite"
                            value={filters.administrationSite}
                            onChange={handleInputChange}
                            placeholder="Filter by Administration Site"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Lot Number</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="lotNumber"
                            value={filters.lotNumber}
                            onChange={handleInputChange}
                            placeholder="Filter by Lot Number"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Manufacturer ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="manufacturerId"
                            value={filters.manufacturerId}
                            onChange={handleInputChange}
                            placeholder="Filter by Manufacturer ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Expiration Date</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            name="expirationDate"
                            value={filters.expirationDate}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Administrator ID</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="administratorId"
                            value={filters.administratorId}
                            onChange={handleInputChange}
                            placeholder="Filter by Administrator ID"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Notes</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="notes"
                            value={filters.notes}
                            onChange={handleInputChange}
                            placeholder="Filter by Notes"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Vaccination ID (Alt)</label>
                        <input 
                            type="text" 
                            className="form-control" 
                            name="vaccinationId"
                            value={filters.vaccinationId}
                            onChange={handleInputChange}
                            placeholder="Filter by Vaccination ID (Alt)"
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Created At</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            name="createdAt"
                            value={filters.createdAt}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Updated At</label>
                        <input 
                            type="date" 
                            className="form-control" 
                            name="updatedAt"
                            value={filters.updatedAt}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div className="col-md-6 mb-3">
                        <label className="form-label">Status</label>
                        <select 
                            className="form-control" 
                            name="status"
                            value={filters.status}
                            onChange={handleInputChange}
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>

                <div className="d-flex justify-content-between">
                    <button 
                        className="btn btn-primary" 
                        onClick={resetFilters}
                    >
                        Clear Filters
                    </button>
                    {onClose && (
                        <button 
                            className="btn btn-secondary" 
                            onClick={onClose}
                        >
                            Close
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VaccinationFilter;